import { useNavigate, useParams } from "react-router-dom";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import SlideDrawer from "../common/SlideDrawer";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { PolicyDocumentItem } from "./api";
import PolicyDocumentsTab from "./tabs/PolicyDocumentsTab";
import PolicyChat from "./PolicyChat";

const TABS = [
  { key: "documents", label: "Documents" },
  { key: "chat", label: "Chat" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

interface Props {
  agencyName: string;
  documents: PolicyDocumentItem[];
  loading: boolean;
}

// The /policies page: header, Documents | Chat tabs, and an Ask drawer, laid
// out like an investigation detail.
const PoliciesHome: React.FC<Props> = ({ agencyName, documents, loading }) => {
  const { tab, threadId } = useParams();
  const navigate = useNavigate();
  // /policies/chat/:threadId has no :tab param, so a thread id also means Chat.
  const activeTab: TabKey = tab === "chat" || threadId ? "chat" : "documents";
  const [chatOpen, setChatOpen] = useLocalStorageState("policies.chatOpen", false);
  const hasDocs = documents.length > 0;
  const latest = documents.reduce<string | null>((acc, d) => {
    const t = d.updatedAt || d.createdAt;
    return t && (!acc || t > acc) ? t : acc;
  }, null);
  const disabledNotice = !loading && !hasDocs ? "Chat opens once your department has uploaded its policies." : null;

  return (
    <div className="flex-1 flex min-h-0">
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-6 pt-4 pb-0 border-b border-base-300">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl font-bold text-base-content truncate">Policies</h1>
              <p className="text-xs text-base-content/60">
                {agencyName || "Your department"}
                {" · "}
                {documents.length} document{documents.length === 1 ? "" : "s"}
                {latest ? ` · updated ${new Date(latest).toLocaleDateString()}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {activeTab !== "chat" && hasDocs && (
                <Button variant={chatOpen ? "primary" : "secondary"} size="sm" onClick={() => setChatOpen(!chatOpen)} data-tour="policies-ask">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" /> Ask
                </Button>
              )}
            </div>
          </div>
          {/* Tab anchors set below by key: data-tour="policies-tab-documents", data-tour="policies-tab-chat" (Academy selectors). */}
          <nav className="mt-3 flex gap-0 -mb-px overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-tour={t.key === "chat" ? "policies-tab-chat" : "policies-tab-documents"}
                onClick={() => navigate(`/policies/${t.key}`)}
                className={cn(
                  "relative px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors",
                  activeTab === t.key ? "border-blue-500 text-blue-600 dark:text-blue-300" : "border-transparent text-base-content/60 hover:text-base-content/80 hover:border-base-content/30"
                )}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>
        {activeTab === "chat" ? (
          <div className="flex-1 min-h-0">
            <PolicyChat agencyName={agencyName} initialThreadId={threadId || null} onThreadChange={(id) => navigate(id ? `/policies/chat/${id}` : "/policies/chat", { replace: true })} disabledNotice={disabledNotice} />
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <div className="mx-auto w-full px-6 py-8 max-w-6xl">
              <PolicyDocumentsTab documents={documents} loading={loading} />
            </div>
          </div>
        )}
      </div>

      <SlideDrawer open={chatOpen && activeTab !== "chat" && hasDocs} storageKey="pnai-policyChatWidth" className="border-l border-base-300 bg-base-100">
        <PolicyChat agencyName={agencyName} compact onClose={() => setChatOpen(false)} />
      </SlideDrawer>
    </div>
  );
};

export default PoliciesHome;
